import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users2, ChevronRight, RefreshCw, Briefcase } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { usePipeline, useUpdateOpportunity } from '@/hooks/useCrm';
import { OpportunityFormDialog } from '@/components/crm/OpportunityFormDialog';
import {
  NEXT_STAGE,
  PIPELINE_STAGES,
  STAGE_LABELS,
  type Opportunity,
  type PipelineStage,
} from '@/types';
import { formatFCFA } from '@/utils/currency';
import { extractError } from '@/api/client';
import { demander } from '@/components/ui/Fenetres';

const STAGE_STYLES: Record<PipelineStage, string> = {
  prospect:    'border-gray-300 bg-gray-50',
  qualification: 'border-blue-300 bg-blue-50',
  proposition: 'border-purple-300 bg-purple-50',
  'négociation': 'border-amber-300 bg-amber-50',
  'gagnée':    'border-emerald-300 bg-emerald-50',
  perdue:      'border-red-300 bg-red-50',
};

const STAGE_HEADER_STYLES: Record<PipelineStage, string> = {
  prospect:    'text-gray-700',
  qualification: 'text-blue-700',
  proposition: 'text-purple-700',
  'négociation': 'text-amber-700',
  'gagnée':    'text-emerald-700',
  perdue:      'text-red-700',
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : null;

export const PipelinePage = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const { data, isLoading, refetch } = usePipeline({ limit: 200 });
  const update = useUpdateOpportunity();

  // Group opportunities by stage
  const byStage = new Map<PipelineStage, Opportunity[]>();
  PIPELINE_STAGES.forEach((s) => byStage.set(s, []));
  (data?.data ?? []).forEach((opp) => {
    const list = byStage.get(opp.stage) ?? [];
    list.push(opp);
    byStage.set(opp.stage, list);
  });

  // Total pondéré par probabilité
  const totalPondereCents = (data?.data ?? []).reduce(
    (sum, opp) => sum + Number(BigInt(opp.amountCents)) * (opp.probabilityPct / 100),
    0,
  );

  const handleAdvance = async (opp: Opportunity) => {
    const next = NEXT_STAGE[opp.stage];
    if (!next) return;
    setActionError(null);
    try {
      // Probabilité par défaut selon le stage suivant
      const newProb = next === 'gagnée' ? 100 : next === 'qualification' ? 30 : next === 'proposition' ? 50 : next === 'négociation' ? 75 : 10;
      await update.mutateAsync({ id: opp.id, payload: { stage: next, probabilityPct: newProb } });
    } catch (err) {
      setActionError(extractError(err).message);
    }
  };

  const handleLose = async (opp: Opportunity) => {
    if (!(await demander.confirmer({ titre: `Marquer « ${opp.title} » comme perdue ?`, confirmer: 'Marquer perdue', danger: true }))) return;
    setActionError(null);
    try {
      await update.mutateAsync({ id: opp.id, payload: { stage: 'perdue', probabilityPct: 0 } });
    } catch (err) {
      setActionError(extractError(err).message);
    }
  };

  return (
    <>
      <Header
        title="Pipeline commercial"
        subtitle={data ? `${data.meta.total} opportunité(s) · pondéré : ${formatFCFA(Math.round(totalPondereCents))}` : 'Suivi des opportunités par stage'}
        actions={
          <>
            <Link to="/crm">
              <Button variant="secondary" size="sm">
                <Users2 className="size-4" /> Contacts
              </Button>
            </Link>
            <Button variant="secondary" size="sm" onClick={() => void refetch()} aria-label="Rafraîchir">
              <RefreshCw className={`size-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="size-4" /> Nouvelle opportunité
            </Button>
          </>
        }
      />

      <div className="space-y-4 p-6">
        {actionError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{actionError}</div>
        )}

        {isLoading ? (
          <div className="text-sm text-gray-400">Chargement…</div>
        ) : data?.data.length === 0 ? (
          <Card>
            <CardContent className="!pt-12 pb-12 text-center">
              <Briefcase className="mx-auto size-10 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">Pipeline vide</p>
              <Button size="sm" className="mt-3" onClick={() => setDialogOpen(true)}>
                <Plus className="size-4" /> Créer la première opportunité
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {PIPELINE_STAGES.map((stage) => {
              const opps = byStage.get(stage) ?? [];
              const stageTotal = opps.reduce((s, o) => s + Number(BigInt(o.amountCents)), 0);
              return (
                <div key={stage} className={`rounded-lg border-2 ${STAGE_STYLES[stage]} p-3 min-h-[400px]`}>
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className={`text-xs font-bold uppercase tracking-wider ${STAGE_HEADER_STYLES[stage]}`}>
                      {STAGE_LABELS[stage]}
                    </h3>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-gray-700">
                      {opps.length}
                    </span>
                  </div>
                  {opps.length > 0 && (
                    <p className="mb-3 text-[10px] tabular-nums text-gray-600">
                      Total : <strong>{formatFCFA(stageTotal)}</strong>
                    </p>
                  )}
                  <div className="space-y-2">
                    {opps.map((opp) => (
                      <div key={opp.id} className="rounded-md border border-gray-200 bg-white p-2.5 text-xs shadow-sm hover:shadow-md transition-shadow">
                        <p className="font-medium text-gray-900 line-clamp-2">{opp.title}</p>
                        {opp.contact && (
                          <p className="mt-1 text-[10px] text-gray-500">
                            {opp.contact.companyName ?? opp.contact.fullName}
                          </p>
                        )}
                        <p className="mt-2 text-sm font-bold tabular-nums text-gray-900">
                          {formatFCFA(opp.amountCents)}
                        </p>
                        <div className="mt-1 flex items-center justify-between text-[10px] text-gray-500">
                          <span>{opp.probabilityPct}% prob.</span>
                          {opp.expectedCloseDate && <span>{fmtDate(opp.expectedCloseDate)}</span>}
                        </div>
                        {NEXT_STAGE[stage] && stage !== 'perdue' && stage !== 'gagnée' && (
                          <div className="mt-2 flex gap-1">
                            <button
                              onClick={() => handleAdvance(opp)}
                              disabled={update.isPending}
                              className="flex-1 inline-flex items-center justify-center gap-1 rounded bg-blue-50 px-2 py-1 text-[10px] font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                              title={`Avancer vers ${STAGE_LABELS[NEXT_STAGE[stage]!]}`}
                            >
                              <ChevronRight className="size-3" />
                              {STAGE_LABELS[NEXT_STAGE[stage]!]}
                            </button>
                            <button
                              onClick={() => handleLose(opp)}
                              disabled={update.isPending}
                              className="rounded bg-red-50 px-2 py-1 text-[10px] font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                              title="Marquer comme perdue"
                            >
                              Perdue
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <OpportunityFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
};
